import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils.js';
import { disabledFade, focusRing } from '../lib/focus.js';

/* ===========================================================================
 * IconButton — a control whose whole label is its icon.
 *
 * `<Button>` covers the labelled case, but an icon-only control has three
 * requirements it doesn't: it must be square (so it reads as a target rather
 * than a cropped button), it must carry an accessible name that no sighted
 * user sees, and it has no text to establish its own height. Every icon-only
 * control in the app was therefore hand-rolled, and the copies drifted:
 *
 *   AppShell theme / language, NotificationBell   rounded-md p-2, no focus
 *                                                 ring, no press state, 34x34
 *   AppShell mobile menu                          h-11 w-11 rounded-control,
 *                                                 focus ring — the good one
 *
 * The 34x34 copies are the app's most-seen controls: they sit in the header on
 * every screen. They missed the design system's focus ring entirely (falling
 * back to the UA outline), used `rounded-md` — the 10px corner design-tokens.css
 * explicitly disavows — and never picked up the coarse-pointer floor, so on the
 * camp tablets and field phones they were 34px against a 44px minimum.
 *
 * `label` is REQUIRED and is not optional decoration: it becomes both the
 * accessible name and the tooltip. There is no way to render this component
 * without one, which is the point — an unlabelled icon button is invisible to a
 * screen reader, and that is exactly the bug this shape invites.
 * ======================================================================== */
const iconButtonVariants = cva(
  cn(
    // `.state-layer` gives the same currentColor hover/focus/press ramp the
    // Button primitive uses, so an icon control darkens and presses like every
    // other control instead of only changing its background on hover.
    'state-layer inline-flex shrink-0 items-center justify-center',
    'rounded-control transition-[background-color,box-shadow,transform,color] duration-fast ease-standard',
    'active:scale-[0.98]',
    focusRing,
    cn('disabled:pointer-events-none', disabledFade),
    // The same finger floor the rest of the control family carries. Desktop
    // keeps console density; a touch device gets a real target.
    '[@media(pointer:coarse)]:min-h-touch [@media(pointer:coarse)]:min-w-touch'
  ),
  {
    variants: {
      variant: {
        ghost: 'text-muted-foreground hover:text-foreground',
        outline: 'border border-border bg-transparent text-foreground',
        danger: 'text-danger focus-visible:outline-danger',
      },
      size: {
        // Square by design — width tracks height so the target is the shape it
        // looks like. `adornment` is the one case that relaxes the width.
        sm: 'h-8 w-8',
        md: 'h-10 w-10',
        lg: 'h-11 w-11',
      },
    },
    defaultVariants: { variant: 'ghost', size: 'md' },
  }
);

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label' | 'title'>,
    VariantProps<typeof iconButtonVariants> {
  /**
   * The control's name. Becomes both `aria-label` and `title` — so the control
   * is announced to a screen reader and explains itself on hover. Required.
   */
  label: string;
  /** The icon. */
  children: ReactNode;
  /**
   * A short trailing mark rendered beside the icon — a language code, a count.
   * Relaxes the square width, since the control is no longer icon-only.
   */
  adornment?: ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant, size, label, adornment, children, ...props }, ref) => (
    <button
      ref={ref}
      // Defaulted here (unlike `<Button>`, which leaves it to the caller): an
      // icon button is a toolbar control, never a form's implicit submit.
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        iconButtonVariants({ variant, size }),
        adornment && 'w-auto gap-1 px-2',
        className
      )}
      {...props}
    >
      {children}
      {adornment}
    </button>
  )
);
IconButton.displayName = 'IconButton';
